import { auth } from "../../firebaseConfig";

// The Fushi assistant is a separate backend service from the main API, so it has
// its own base URL. Falls back to local dev if the env var is unset.
const BASE_URL = process.env.REACT_APP_FUSHI_API_URL || "https://fushi-8nmp.onrender.com";

// Attach the signed-in user's Firebase ID token when available. Signed-out users
// get an empty header — chat still works, the JWT only unlocks booking lookups.
async function authHeader(forceRefresh = false) {
  const currentUser = auth.currentUser;
  if (!currentUser) return {};
  try {
    const token = await currentUser.getIdToken(forceRefresh);
    return { Authorization: `Bearer ${token}` };
  } catch (e) {
    console.warn("fushiClient: could not get Firebase token", e);
    return {};
  }
}

// Turn a Fushi error body into a human-readable string.
// 502 → { detail: "..." }; 422 → { detail: [{ msg: "..." }, ...] }.
function extractError(body, status) {
  const detail = body?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msg = detail.map((d) => d?.msg).filter(Boolean).join(", ");
    if (msg) return msg;
  }
  return `Fushi error (${status})`;
}

/**
 * Send a message to Fushi.
 * Omit sessionId on the first turn — the server creates one and returns it.
 * Reuse the returned id on every follow-up to continue the conversation.
 * @returns {Promise<{ session_id: string, reply: string }>}
 */
export async function sendChat({ message, sessionId }) {
  const body = { message };
  if (sessionId) body.session_id = sessionId; // omit on the first message

  const doFetch = async (forceRefresh) =>
    fetch(`${BASE_URL}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeader(forceRefresh)) },
      body: JSON.stringify(body),
    });

  let res = await doFetch(false);

  // Token may have expired mid-session — retry once with a fresh one.
  if (res.status === 401 && auth.currentUser) {
    res = await doFetch(true);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    throw new Error(extractError(errBody, res.status));
  }

  return res.json();
}

/** Best-effort session cleanup. Safe to call with a missing/unknown id. */
export async function deleteSession(sessionId) {
  if (!sessionId) return;
  try {
    await fetch(`${BASE_URL}/sessions/${sessionId}`, {
      method: "DELETE",
      headers: await authHeader(),
    });
  } catch (e) {
    // Non-critical — the server drops in-memory sessions on its own anyway.
    console.warn("fushiClient: session cleanup failed", e);
  }
}
